import React, { useState } from 'react';
import axios from 'axios';
import { Hash, AlertTriangle, RefreshCw, Loader2 } from 'lucide-react';
import { useToast } from '../../hooks/use-toast';

const API = `${window.__BACKEND_URL__}/api`;

// One-click re-push of the HinesTime invoice number to the existing QBO invoice.
export const UpdateQboNumberButton = ({ job, onDone, className = '' }) => {
  const { toast } = useToast();
  const [busy, setBusy] = useState(false);
  const run = async (e) => {
    e?.stopPropagation?.();
    setBusy(true);
    try {
      const { data } = await axios.post(`${API}/qb/update-invoice-number/${job.id}`, {}, { headers: { Authorization: `Bearer ${localStorage.getItem('token')}` } });
      toast({ title: data.matched ? 'QuickBooks number updated' : 'QuickBooks kept its own number', description: data.message, variant: data.matched ? undefined : 'destructive' });
      onDone?.({ ...job, qb_invoice_doc_number: data.doc_number });
    } catch (err) {
      toast({ title: 'Could not update QuickBooks number', description: err.response?.data?.detail || err.message, variant: 'destructive' });
    } finally { setBusy(false); }
  };
  return (
    <button type="button" onClick={run} disabled={busy}
      className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-semibold disabled:opacity-60 ${className}`}
      style={{ background: '#1E88E5', color: '#fff' }} title="Re-push the HinesTime invoice number to this QuickBooks invoice"
      data-testid={`qbo-update-number-${job.id}`}>
      {busy ? <Loader2 className="w-3 h-3 animate-spin" /> : <RefreshCw className="w-3 h-3" />} Update QuickBooks number
    </button>
  );
};

// Job card chip: HinesTime invoice number + a warning (and fix button) when QuickBooks kept a different number.
export const InvoiceNumberBadge = ({ job, onUpdated }) => {
  const [local, setLocal] = useState(null);
  const j = local || job;
  if (!j?.ht_invoice_number) return null;
  const qbo = (j.qb_invoice_doc_number || '').trim();
  const mismatch = !!j.qb_invoice_id && !!qbo && qbo !== j.ht_invoice_number;
  return (
    <span className="inline-flex items-center gap-1.5 flex-wrap">
      <span className="ht-pill text-xs inline-flex items-center gap-1" style={{ background: 'rgba(30,136,229,0.15)', color: '#90CAF9' }}
            title="HinesTime invoice number" data-testid={`ht-invoice-number-${j.id}`}>
        <Hash className="w-3 h-3" />{j.ht_invoice_number}
      </span>
      {mismatch && (
        <>
          <span className="ht-pill text-xs inline-flex items-center gap-1" style={{ background: 'rgba(255,160,0,0.15)', color: '#FFB74D' }}
                title={`QuickBooks numbered this invoice ${qbo} instead of ${j.ht_invoice_number}. Turn on Custom transaction numbers in QuickBooks (Settings → Sales).`}
                data-testid={`qbo-number-mismatch-${j.id}`}>
            <AlertTriangle className="w-3 h-3" />QBO #{qbo} ≠ {j.ht_invoice_number}
          </span>
          <UpdateQboNumberButton job={j} onDone={(nj) => { setLocal(nj); onUpdated?.(nj); }} />
        </>
      )}
    </span>
  );
};
